/**
 * Pomodoro Controls — mode dots + start/pause pill.
 * Minimal: 3 dots for mode, one big button.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Play, Pause, SkipForward } from 'lucide-react'
import { usePomodoroStore } from '@turnifly/shared/stores'
import type { PomodoroMode } from '@turnifly/shared/stores'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

const MODES: PomodoroMode[] = ['work', 'short_break', 'long_break']

export const PomodoroControls: React.FC = () => {
  const { t } = useTranslation()
  const mode = usePomodoroStore((s) => s.mode)
  const status = usePomodoroStore((s) => s.status)
  const setMode = usePomodoroStore((s) => s.setMode)
  const startTimer = usePomodoroStore((s) => s.startTimer)
  const pauseTimer = usePomodoroStore((s) => s.pauseTimer)
  const resumeTimer = usePomodoroStore((s) => s.resumeTimer)
  const skipToNext = usePomodoroStore((s) => s.skipToNext)

  const activeHabitId = usePomodoroStore((s) => s.activeHabitId)
  const activeTaskId = usePomodoroStore((s) => s.activeTaskId)
  const hasContext = !!(activeHabitId || activeTaskId)

  const handleMain = (): void => {
    hapticImpact('medium')
    if (status === 'idle') {
      if (!hasContext) {
        // Tanlash kerak — menu ochiladi
        hapticImpact('light')
        return
      }
      startTimer()
    } else if (status === 'running') pauseTimer()
    else resumeTimer()
  }

  const handleSkip = (): void => {
    hapticImpact('light')
    skipToNext()
  }

  const handleDotClick = (m: PomodoroMode): void => {
    if (status !== 'idle') return
    setMode(m)
  }

  const modeLabel = (m: PomodoroMode): string => {
    if (m === 'work') return t('pomodoro.pomodoro')
    if (m === 'short_break') return t('pomodoro.shortBreak')
    return t('pomodoro.longBreak')
  }

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Mode dots -> Segmented Control */}
      <div className="relative flex items-center p-1 rounded-full bg-black/20 backdrop-blur-xl border border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.1)] mx-auto">
        {MODES.map((m) => {
          const isActive = mode === m
          return (
            <button
              key={m}
              onClick={() => handleDotClick(m)}
              disabled={status !== 'idle'}
              className={`relative z-10 px-4 py-1.5 min-w-[80px] rounded-full text-[11px] font-semibold transition-all duration-300 tap-highlight-transparent ${
                isActive ? 'text-[#0e1621]' : 'text-white/60 hover:text-white/90'
              } ${status !== 'idle' && !isActive ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activePomodoroMode"
                  className="absolute inset-0 bg-white/95 rounded-full shadow-sm z-[-1]"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                />
              )}
              {modeLabel(m)}
            </button>
          )
        })}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {/* Main button */}
        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={handleMain}
          className="h-14 px-12 rounded-full bg-white/95 backdrop-blur-xl text-[#0e1621] font-bold text-[15px] flex items-center gap-2.5 shadow-[0_8px_32px_rgba(255,255,255,0.25)] tap-highlight-transparent border border-white"
        >
          {status === 'running' ? (
            <>
              <Pause size={18} fill="currentColor" />
              {t('pomodoro.pause')}
            </>
          ) : status === 'paused' ? (
            <>
              <Play size={18} fill="currentColor" />
              {t('pomodoro.resume')}
            </>
          ) : !hasContext ? (
            <span className="text-[13px] opacity-70 font-semibold">{t('pomodoro.selectTask')}</span>
          ) : (
            <>
              <Play size={18} fill="currentColor" />
              {t('pomodoro.start')}
            </>
          )}
        </motion.button>

        {/* Skip (only during active session) */}
        {status !== 'idle' && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleSkip}
            className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center tap-highlight-transparent"
          >
            <SkipForward size={16} className="text-white/70" />
          </motion.button>
        )}
      </div>
    </div>
  )
}
